import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { Pencil, Trash2 } from "lucide-react";
import type { PromotionPathRuleListItem } from "@/modules/hr/promotion/domain/types";

type Props = {
  data: PromotionPathRuleListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onEdit: (row: PromotionPathRuleListItem) => void;
  onDelete?: (row: PromotionPathRuleListItem) => void;
};

export function PromotionRulesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onEdit,
  onDelete,
}: Props) {
  const columns: TanstackReactTable.ColumnDef<PromotionPathRuleListItem>[] = [
    createSortableColumn<PromotionPathRuleListItem>(
      "fromPositionTitle",
      "ຈາກຕຳແໜ່ງ",
      {
        size: 180,
        cell: ({ row }) => (
          <span className="font-medium">{row.original.fromPositionTitle}</span>
        ),
      },
    ),
    createSortableColumn<PromotionPathRuleListItem>(
      "toPositionTitle",
      "ໄປຕຳແໜ່ງ",
      {
        size: 180,
      },
    ),
    createSortableColumn<PromotionPathRuleListItem>(
      "requiredYears",
      "ປະສົບການ (ປີ)",
      {
        size: 140,
        cell: ({ row }) => row.original.requiredYears ?? "-",
      },
    ),
    createSortableColumn<PromotionPathRuleListItem>("isActive", "ສະຖານະ", {
      size: 110,
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ACTIVE" : "INACTIVE"}
        </Badge>
      ),
    }),
    createSortableColumn<PromotionPathRuleListItem>("createdByName", "ຜູ້ສ້າງ", {
      size: 140,
      cell: ({ row }) => row.original.createdByName ?? "-",
    }),
    createSortableColumn<PromotionPathRuleListItem>("createdAt", "ສ້າງວັນທີ", {
      size: 160,
      cell: ({ row }) =>
        new Date(row.original.createdAt).toLocaleDateString("en-GB"),
    }),
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(row.original)}>
            <Pencil className="h-4 w-4" />
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(row.original)}
              aria-label="ລຶບຂໍ້ກຳນົດ"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable<PromotionPathRuleListItem, unknown>
        noDataMessage="ບໍ່ພົບຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          onSortingChange(sorting[0]?.id ?? "createdAt", !!sorting[0]?.desc);
        }}
      />
  );
}
